import { useTheme } from "@emotion/react";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { createColumnHelper } from "@tanstack/table-core";
import { BillingRuleEntity } from "@ternary/api-lib/core/types/BillingRule";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import { RowDragHandleCell } from "@ternary/api-lib/ui-lib/components/Table/RowDragHandleCell";
import Table from "@ternary/api-lib/ui-lib/components/Table/Table";
import Box from "@ternary/web-ui-lib/components/Box";
import Icon from "@ternary/web-ui-lib/components/Icon";
import Text from "@ternary/web-ui-lib/components/Text";
import { formatDate } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import React, { useMemo } from "react";
import useGatekeeper from "../../../hooks/useGatekeeper";
import Dropdown from "../../../ui-lib/components/Dropdown";
import copyText from "../copyText";
import { MinimalRule } from "./BillingRuleForm/types";

type FullRule = BillingRuleEntity & { createdByEmail?: string };

interface BaseProps {
  isLoading: boolean;
  isMspAdmin?: boolean;
  onInteraction: (interaction: BillingRuleTable.Interaction) => void;
}

interface MinimalProps extends BaseProps {
  minimal: true;
  rules: MinimalRule[];
}

interface FullProps extends BaseProps {
  minimal?: false;
  rules: FullRule[];
}

type Props = MinimalProps | FullProps;

type TableData = {
  id: string;
  applyToAllTenants: boolean;
  createdBy?: string;
  endTime: string | null;
  name: string;
  priority: number;
  startTime: string;
  tenantCount: number;
  timeLastModified: string;
};

const columnHelper = createColumnHelper<TableData>();

export function BillingRuleTable(props: Props) {
  const gatekeeper = useGatekeeper();
  const theme = useTheme();

  const columns = useMemo(() => {
    const minimalColumns = [
      columnHelper.accessor("priority", {
        cell: (context) => (
          <RowDragHandleCell row={context.row}>
            <Text>{context.row.original.priority}</Text>
          </RowDragHandleCell>
        ),
        header: copyText.tableHeaderExecutionOrder,
      }),
      columnHelper.accessor("name", {
        cell: (context) =>
          context.row.original.id === "new" ? (
            <Flex>
              <Text>{context.getValue()}</Text>
              <Box
                border="1px solid #B9E5FF"
                borderRadius={theme.borderRadius_1}
                height={22}
                marginLeft={theme.space_xs}
                padding={"2px 6px"}
                width="fit-content"
              >
                <Text
                  color="#0A68EB"
                  fontWeight={theme.fontWeight_semiBold}
                  fontSize={"12px"}
                >
                  {copyText.newBillingRuleLabel}
                </Text>
              </Box>
            </Flex>
          ) : (
            context.getValue()
          ),
        header: copyText.tableHeaderName,
        size: 250,
      }),
    ];

    // If minimal table for form, only render minimal columns
    if (props.minimal) return minimalColumns;

    const restColumns = [
      ...(props.isMspAdmin
        ? [
            columnHelper.display({
              cell: (context) => {
                const applyToAllTenants =
                  context.row.original.applyToAllTenants;
                const tenantCount = context.row.original.tenantCount;

                return applyToAllTenants
                  ? copyText.allTenantsLabel
                  : tenantCount;
              },
              header: copyText.tableHeaderTenants,
              size: 60,
            }),
          ]
        : []),
      columnHelper.accessor("createdBy", {
        header: copyText.tableHeaderCreatedBy,
      }),
      columnHelper.accessor("timeLastModified", {
        cell: (context) => (
          <>{formatDate(new Date(context.getValue()), "MM/dd/yyyy hh:mm a")}</>
        ),
        header: copyText.tableHeaderLastModified,
        size: 160,
      }),
      columnHelper.display({
        cell: ({ row }) => (
          <>
            {`${formatDate(toZonedTime(new Date(row.original.startTime), "UTC"), "MM/dd/yyyy")} - ${
              row.original.endTime
                ? formatDate(
                    toZonedTime(new Date(row.original.endTime), "UTC"),
                    "MM/dd/yyyy"
                  )
                : copyText.presentDateLabel
            }`}
          </>
        ),
        header: copyText.tableHeaderDateRange,
        size: 250,
      }),
      columnHelper.display({
        id: "actionMenu",
        cell: function renderButton({ row }) {
          const dropdownOptions = [
            {
              disabled:
                !gatekeeper.canAccessMspAdmin &&
                !gatekeeper.canUpdateBillingRule,
              label: copyText.actionEdit,
              onClick: () =>
                props.onInteraction({
                  type: BillingRuleTable.INTERACTION_EDIT_CLICKED,
                  ruleID: row.original.id,
                }),
            },
            {
              disabled:
                !gatekeeper.canAccessMspAdmin &&
                !gatekeeper.canDeleteBillingRule,
              label: copyText.actionDelete,
              onClick: () =>
                props.onInteraction({
                  type: BillingRuleTable.INTERACTION_DELETE_CLICKED,
                  ruleSetID: row.original.id,
                }),
            },
          ];

          return (
            <Dropdown options={dropdownOptions} placement="bottom-end">
              <Button
                iconStart={<Icon icon={faEllipsisV} />}
                secondary
                size="tiny"
              />
            </Dropdown>
          );
        },
        size: 30,
      }),
    ];

    return [...minimalColumns, ...restColumns];
  }, [props.rules]);

  const data = useMemo((): TableData[] => {
    if (props.minimal) {
      return props.rules.map((rule) => ({
        id: rule.id,
        name: rule.name,
        priority: rule.priority,
      })) as TableData[];
    }

    return props.rules.map((rule) => ({
      id: rule.id,
      applyToAllTenants: rule.applyToAllTenants,
      createdBy: rule.createdByEmail ?? rule.createdByID,
      endTime: rule.endTime,
      name: rule.name,
      priority: rule.priority,
      startTime: rule.startTime,
      tenantCount: rule.applyToTenantIDs.length,
      timeLastModified: rule.updatedAt ?? rule.createdAt,
    }));
  }, [props.rules]);

  return (
    <>
      <Box height={500}>
        <Table
          columns={columns}
          data={data}
          draggableRows
          initialState={{
            pagination: { pageSize: 10 },
            sorting: [{ id: "priority", desc: false }],
          }}
          isLoading={props.isLoading}
          showPagination
          sortable
          onChangeRowOrder={(_, { newIndex, oldIndex }) =>
            props.onInteraction({
              type: BillingRuleTable.INTERACTION_ROW_ORDER_CHANGED,
              newIndex,
              oldIndex,
            })
          }
        />
      </Box>
    </>
  );
}

BillingRuleTable.INTERACTION_CREATE_CLICKED =
  `BillingRuleTable.INTERACTION_CREATE_CLICKED` as const;
BillingRuleTable.INTERACTION_DELETE_CLICKED =
  `BillingRuleTable.INTERACTION_DELETE_CLICKED` as const;
BillingRuleTable.INTERACTION_EDIT_CLICKED =
  `BillingRuleTable.INTERACTION_EDIT_CLICKED` as const;
BillingRuleTable.INTERACTION_ROW_ORDER_CHANGED =
  `BillingRuleTable.INTERACTION_ROW_ORDER_CHANGED` as const;

interface InteractionCreateClicked {
  type: typeof BillingRuleTable.INTERACTION_CREATE_CLICKED;
}

interface InteractionDeleteClicked {
  type: typeof BillingRuleTable.INTERACTION_DELETE_CLICKED;
  ruleSetID: string;
}

interface InteractionEditClicked {
  type: typeof BillingRuleTable.INTERACTION_EDIT_CLICKED;
  ruleID: string;
}

interface InteractionRowOrderChanged {
  type: typeof BillingRuleTable.INTERACTION_ROW_ORDER_CHANGED;
  newIndex: number;
  oldIndex: number;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace BillingRuleTable {
  export type Interaction =
    | InteractionCreateClicked
    | InteractionDeleteClicked
    | InteractionEditClicked
    | InteractionRowOrderChanged;
}
